import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import type { InvitationCodePreview, MemberManagementViewModel } from '../appService';
import type { JoinRequest } from '../model';
import { useAppRuntime, useBootstrapContext } from './context';
import { APP_BOOTSTRAP_QUERY_KEY, useContextMutationReset } from './data';
import {
  ActionCard,
  ActionError,
  BackHeader,
  ConfirmedField,
  OwnJoinRequests,
  PendingButton,
  TextField,
  formatActionError
} from './ui';

type AuthStep =
  | { readonly name: 'email'; readonly email: string }
  | { readonly name: 'login'; readonly email: string }
  | { readonly name: 'register'; readonly email: string }
  | { readonly name: 'forgot-password'; readonly email: string };

type OnboardingStep =
  | { readonly name: 'choice' }
  | { readonly name: 'create-choir' }
  | { readonly name: 'join-code' }
  | { readonly name: 'join-nickname'; readonly preview: InvitationCodePreview }
  | { readonly name: 'join-submitted'; readonly joinRequest: JoinRequest | null }
  | { readonly name: 'choose-existing-choir' };

export function AuthPage(): ReactNode {
  const { service } = useAppRuntime();
  const resetAfterContextMutation = useContextMutationReset();
  const [step, setStep] = useState<AuthStep>({ name: 'email', email: '' });
  const [email, setEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const identifyEmail = useMutation({
    mutationFn: (input: { readonly email: string }) => service.identifyEmail(input)
  });
  const login = useMutation({
    mutationFn: (input: { readonly email: string; readonly password: string }) => service.login(input)
  });
  const register = useMutation({
    mutationFn: (input: { readonly email: string; readonly password: string }) => service.register(input)
  });

  const clearError = (): void => {
    setActionError(null);
  };
  const goToEmailStep = (nextEmail: string): void => {
    clearError();
    setEmail(nextEmail);
    setStep({ name: 'email', email: nextEmail });
  };

  if (step.name === 'email') {
    return (
      <div className="route-outlet auth-route" data-screen="auth-email">
        <header className="auth-header">
          <span className="auth-logo" aria-hidden="true">
            CN
          </span>
          <p className="auth-brand">Choir Nest</p>
          <h1 className="page-title" tabIndex={-1}>
            注册或登录
          </h1>
          <p>输入邮箱继续。</p>
        </header>
        <div className="form-stack">
          <TextField
            label="邮箱"
            field="auth-email"
            name="email"
            type="email"
            autoComplete="email"
            spellCheck={false}
            value={email}
            onChange={setEmail}
          />
          <ActionError message={actionError} />
          <PendingButton
            action="identify-email"
            label="下一步"
            variant="primary"
            pending={identifyEmail.isPending}
            onClick={() => {
              void (async () => {
                clearError();
                try {
                  const result = await identifyEmail.mutateAsync({ email });
                  setEmail(result.email);
                  setStep(
                    result.status === 'registered'
                      ? { name: 'login', email: result.email }
                      : { name: 'register', email: result.email }
                  );
                } catch (error) {
                  setActionError(formatActionError(error));
                }
              })();
            }}
          />
          <p className="auth-helper">继续即表示你同意使用邮箱作为账号标识</p>
        </div>
      </div>
    );
  }

  if (step.name === 'login') {
    return (
      <div className="route-outlet" data-screen="auth-login">
        <BackHeader
          title="输入密码"
          action="back-to-email"
          label="返回注册或登录"
          onBack={() => goToEmailStep(step.email)}
        />
        <div className="form-stack">
          <ConfirmedField label="邮箱" value={step.email} />
          <TextField
            label="密码"
            field="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            spellCheck={false}
            value={loginPassword}
            onChange={setLoginPassword}
          />
          <ActionError message={actionError} />
          <PendingButton
            action="login"
            label="登录"
            variant="primary"
            pending={login.isPending}
            onClick={() => {
              void (async () => {
                clearError();
                try {
                  await login.mutateAsync({
                    email: step.email,
                    password: loginPassword
                  });
                  setStep({ name: 'email', email: '' });
                  setEmail('');
                  setLoginPassword('');
                  await resetAfterContextMutation();
                } catch (error) {
                  setActionError(formatActionError(error));
                }
              })();
            }}
          />
          <button
            type="button"
            className="text-button"
            data-action="forgot-password"
            onClick={() => {
              clearError();
              setStep({ name: 'forgot-password', email: step.email });
            }}
          >
            忘记密码
          </button>
        </div>
      </div>
    );
  }

  if (step.name === 'register') {
    return (
      <div className="route-outlet" data-screen="auth-register">
        <BackHeader
          title="创建账号"
          action="back-to-email"
          label="返回注册或登录"
          onBack={() => goToEmailStep(step.email)}
        />
        <div className="form-stack">
          <ConfirmedField label="邮箱" value={step.email} />
          <TextField
            label="密码"
            field="register-password"
            name="password"
            type="password"
            autoComplete="new-password"
            spellCheck={false}
            value={registerPassword}
            onChange={setRegisterPassword}
          />
          <ActionError message={actionError} />
          <PendingButton
            action="register"
            label="创建账号"
            variant="primary"
            pending={register.isPending}
            onClick={() => {
              void (async () => {
                clearError();
                try {
                  await register.mutateAsync({
                    email: step.email,
                    password: registerPassword
                  });
                  setStep({ name: 'email', email: '' });
                  setEmail('');
                  setRegisterPassword('');
                  await resetAfterContextMutation();
                } catch (error) {
                  setActionError(formatActionError(error));
                }
              })();
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="route-outlet" data-screen="auth-forgot-password">
      <BackHeader
        title="找回密码"
        action="back-to-login"
        label="返回输入密码"
        onBack={() => {
          clearError();
          setStep({ name: 'login', email: step.email });
        }}
      />
      <section className="account-notice">
        <p>当前暂不支持自助找回密码，请联系合唱团管理员或项目运营。</p>
      </section>
    </div>
  );
}

export function OnboardingPage(): ReactNode {
  const { service } = useAppRuntime();
  const bootstrap = useBootstrapContext();
  const queryClient = useQueryClient();
  const resetAfterContextMutation = useContextMutationReset();
  const [step, setStep] = useState<OnboardingStep>({ name: 'choice' });
  const [choirName, setChoirName] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [nickname, setNickname] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const logout = useMutation({
    mutationFn: () => service.logout()
  });
  const createChoir = useMutation({
    mutationFn: (input: { readonly name: string }) => service.createChoir(input)
  });
  const previewInvitationCode = useMutation({
    mutationFn: (input: { readonly code: string }) => service.previewInvitationCode(input)
  });
  const submitJoinRequest = useMutation({
    mutationFn: (input: { readonly code: string; readonly nickname: string }) => service.submitJoinRequest(input)
  });
  const switchChoir = useMutation({
    mutationFn: (input: { readonly choirId: string }) => service.switchChoir(input)
  });

  const clearError = (): void => {
    setActionError(null);
  };
  const goToChoice = (): void => {
    clearError();
    setStep({ name: 'choice' });
  };
  const runSwitchChoir = (choirId: string): void => {
    if (switchChoir.isPending) {
      return;
    }
    void (async () => {
      clearError();
      try {
        await switchChoir.mutateAsync({ choirId });
        setStep({ name: 'choice' });
        await resetAfterContextMutation();
      } catch (error) {
        setActionError(formatActionError(error));
      }
    })();
  };

  if (step.name === 'choice') {
    return (
      <div className="route-outlet" data-screen="onboarding-choice">
        <BackHeader
          title="开始你的合唱团空间"
          action="logout"
          label="返回注册或登录"
          pending={logout.isPending}
          onBack={() => {
            void (async () => {
              clearError();
              try {
                await logout.mutateAsync();
                setStep({ name: 'choice' });
                await resetAfterContextMutation();
              } catch (error) {
                setActionError(formatActionError(error));
              }
            })();
          }}
        />
        <ActionError message={actionError} />
        <div className="action-list">
          <ActionCard
            action="choose-create-choir"
            label="创建合唱团"
            onClick={() => {
              clearError();
              setStep({ name: 'create-choir' });
            }}
          />
          <ActionCard
            action="choose-join-choir"
            label="申请入团"
            onClick={() => {
              clearError();
              setStep({ name: 'join-code' });
            }}
          />
          {bootstrap.appContext?.joinedChoirs.length ? (
            <ActionCard
              action="choose-existing-choir"
              label="我的合唱团"
              pending={switchChoir.isPending}
              onClick={() => {
                const choirs = bootstrap.appContext?.joinedChoirs ?? [];
                if (choirs.length === 1) {
                  runSwitchChoir(choirs[0].choirId);
                  return;
                }
                clearError();
                setStep({ name: 'choose-existing-choir' });
              }}
            />
          ) : null}
        </div>
        <OwnJoinRequests requests={bootstrap.appContext?.ownJoinRequests ?? []} />
      </div>
    );
  }

  if (step.name === 'create-choir') {
    return (
      <div className="route-outlet" data-screen="onboarding-create-choir">
        <BackHeader title="创建合唱团" action="back-to-entry-choice" label="返回合唱团空间入口" onBack={goToChoice} />
        <div className="form-stack">
          <TextField
            label="合唱团名称"
            field="choir-name"
            name="choirName"
            autoComplete="organization"
            value={choirName}
            onChange={setChoirName}
          />
          <ActionError message={actionError} />
          <PendingButton
            action="create-choir"
            label="创建并进入"
            variant="primary"
            pending={createChoir.isPending}
            onClick={() => {
              void (async () => {
                clearError();
                try {
                  await createChoir.mutateAsync({ name: choirName });
                  setStep({ name: 'choice' });
                  await resetAfterContextMutation();
                } catch (error) {
                  setActionError(formatActionError(error));
                }
              })();
            }}
          />
        </div>
      </div>
    );
  }

  if (step.name === 'join-code') {
    return (
      <div className="route-outlet" data-screen="onboarding-join-code">
        <BackHeader title="申请入团" action="back-to-entry-choice" label="返回合唱团空间入口" onBack={goToChoice} />
        <div className="form-stack">
          <TextField
            label="邀请码"
            field="join-code"
            name="invitationCode"
            autoComplete="one-time-code"
            spellCheck={false}
            value={invitationCode}
            onChange={setInvitationCode}
          />
          <ActionError message={actionError} />
          <PendingButton
            action="preview-invitation-code"
            label="验证邀请码"
            variant="primary"
            pending={previewInvitationCode.isPending}
            onClick={() => {
              void (async () => {
                clearError();
                try {
                  const preview = await previewInvitationCode.mutateAsync({ code: invitationCode });
                  setStep({ name: 'join-nickname', preview });
                } catch (error) {
                  setActionError(formatActionError(error));
                }
              })();
            }}
          />
        </div>
      </div>
    );
  }

  if (step.name === 'join-nickname') {
    return (
      <div className="route-outlet" data-screen="onboarding-join-nickname">
        <BackHeader title="提交加入申请" action="back-to-entry-choice" label="返回合唱团空间入口" onBack={goToChoice} />
        <article className="entry-info-card">
          <strong>{step.preview.choirName}</strong>
          <small>{step.preview.seasonName}</small>
        </article>
        <div className="form-stack">
          <TextField
            label="昵称"
            field="join-nickname"
            name="nickname"
            autoComplete="nickname"
            value={nickname}
            onChange={setNickname}
          />
          <ActionError message={actionError} />
          <PendingButton
            action="submit-join-request"
            label="提交申请"
            variant="primary"
            pending={submitJoinRequest.isPending}
            onClick={() => {
              void (async () => {
                clearError();
                try {
                  const joinRequest = await submitJoinRequest.mutateAsync({
                    code: step.preview.code,
                    nickname
                  });
                  queryClient.setQueryData<MemberManagementViewModel>(APP_BOOTSTRAP_QUERY_KEY, (current) =>
                    current
                      ? {
                          ...current,
                          ownJoinRequests: [
                            ...current.ownJoinRequests.filter(
                              (candidate) => candidate.joinRequestId !== joinRequest.joinRequestId
                            ),
                            joinRequest
                          ]
                        }
                      : current
                  );
                  setStep({ name: 'join-submitted', joinRequest });
                } catch (error) {
                  setActionError(formatActionError(error));
                }
              })();
            }}
          />
        </div>
      </div>
    );
  }

  if (step.name === 'join-submitted') {
    return (
      <div className="route-outlet" data-screen="onboarding-join-submitted">
        <BackHeader title="申请状态" action="back-to-entry-choice" label="返回合唱团空间入口" onBack={goToChoice} />
        <section className="entry-result">
          <span className="entry-result-icon" aria-hidden="true">
            ✓
          </span>
          <h2>申请已提交</h2>
          <p>等待管理员处理，审批通过后进入当前乐季。</p>
          <button type="button" className="secondary-button" data-action="back-to-entry-choice" onClick={goToChoice}>
            返回
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="route-outlet" data-screen="onboarding-choose-existing-choir">
      <BackHeader title="我的合唱团" action="back-to-entry-choice" label="返回合唱团空间入口" onBack={goToChoice} />
      <ActionError message={actionError} />
      <div className="form-stack">
        {(bootstrap.appContext?.joinedChoirs ?? []).map((choir) => (
          <PendingButton
            key={choir.choirId}
            action="switch-choir"
            label={choir.name}
            variant="secondary"
            pending={switchChoir.isPending}
            dataAttrs={{ choirId: choir.choirId }}
            onClick={() => runSwitchChoir(choir.choirId)}
          />
        ))}
      </div>
    </div>
  );
}
